import { useEffect, useState } from "react"
import { ButtonTranslucent } from "@nova/component-btn"
import { Dropdown } from "@nova/component-dropdown"
import { zIndex } from "@nova/constants"
import * as Icons from "@nova/component-icons"
import { useMapFeatures, useMapBackground, MapBackgroundViewType } from "../control/useMapStyles"
import { MapStyleNames } from "../control/module"
import { useViewport } from "../GlobalViewState"
import { setOverlayEnabled, useOverlays } from "../control/useOverlays"
import { OverlayKey } from "../control/types"
import { GEOSTATIONARY_FADE_OUT_ZOOM } from "../layers/overlays/useGeostationaryLayer"
import { MapFooterLayerCheckboxRow } from "./MapFooterLayerCheckboxRow"
import { MapFooterBaseMapRow } from "./MapFooterBaseMapRow"
import { MapFooterOverlayInfoPanel, HoveredOverlayRow } from "./MapFooterOverlayInfoPanel"

const SectionLabel = ({ label, className = "" }: { label: string; className?: string }) => (
    <small className={`block uppercase tracking-[1.2px] text-gray-300 leading-[18px] ${className}`}>
        {label}
    </small>
)

const getOverlayRowDefinitions = (): {
    key: OverlayKey
    title: string
    Icon: Icons.IconComponent
    viewTypes: MapBackgroundViewType[]
    disabledPastZoom?: number
}[] => [
    {
        key: "heat",
        title: "Heat",
        Icon: Icons.Heat,
        viewTypes: ["project"],
    },
    {
        key: "firePerimeters",
        title: "Wildfires",
        Icon: Icons.Wildfires,
        viewTypes: ["project"],
    },
    {
        key: "fireDanger",
        title: "Fire Danger",
        Icon: Icons.FireDanger,
        viewTypes: ["overview"],
    },
    {
        key: "civilization",
        title: "Civilization",
        Icon: Icons.Civilization,
        viewTypes: ["project", "overview"],
    },
    {
        key: "elevation",
        title: "Elevation",
        Icon: Icons.Elevation,
        viewTypes: ["project", "overview"],
    },
    {
        key: "geostationary",
        title: "Geostationary",
        Icon: Icons.Geostationary,
        viewTypes: ["overview"],
        disabledPastZoom: GEOSTATIONARY_FADE_OUT_ZOOM,
    },
]

const useOverlayRows = (viewType: MapBackgroundViewType) => {
    const overlays = useOverlays(viewType)
    const zoom = useViewport().zoom
    return getOverlayRowDefinitions()
        .filter((definition) => definition.viewTypes.includes(viewType))
        .map((definition) => {
            const overlay = overlays[definition.key]
            const overzoomed =
                definition.disabledPastZoom !== undefined && zoom > definition.disabledPastZoom
            return {
                key: definition.key,
                title: definition.title,
                Icon: definition.Icon,
                isChecked: overlay.enabled,
                status: overlay.enabled ? overlay.status : undefined,
                disabled: !overlay.enabled && overzoomed,
                overzoomed,
                onClick: () => setOverlayEnabled(viewType, definition.key, !overlay.enabled),
            }
        })
}

const useBasemapRows = (viewType: MapBackgroundViewType) => {
    const { mapBackgrounds, background: currentBackground } = useMapBackground(viewType)
    const defaultStyle = viewType === "overview" ? MapStyleNames.Dark : MapStyleNames.Light

    useEffect(() => {
        if (
            (currentBackground === MapStyleNames.Light ||
                currentBackground === MapStyleNames.Dark) &&
            currentBackground !== defaultStyle
        ) {
            mapBackgrounds.find((background) => background.label === defaultStyle)?.switchTo()
        }
    }, [currentBackground, defaultStyle, mapBackgrounds])

    const defaultBackground = mapBackgrounds.find((background) => background.label === defaultStyle)
    const isDefaultSelected = mapBackgrounds.some(
        (background) =>
            (background.label === MapStyleNames.Light || background.label === MapStyleNames.Dark) &&
            background.isActiveBackground
    )
    return defaultBackground
        ? [
              { ...defaultBackground, title: "Default", isActiveBackground: isDefaultSelected },
              ...mapBackgrounds.filter(
                  (background) =>
                      background.label === MapStyleNames.Satellite ||
                      background.label === MapStyleNames.White
              ),
          ]
        : []
}

export const MapFooterLayersPanel = ({
    viewType,
    disabled,
}: {
    viewType: MapBackgroundViewType
    disabled?: boolean
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [hoveredOverlay, setHoveredOverlay] = useState<HoveredOverlayRow | null>(null)
    const mapFeatures = useMapFeatures()
    const { isLight } = useMapBackground(viewType)
    const overlayRows = useOverlayRows(viewType)
    const basemapRows = useBasemapRows(viewType)

    useEffect(() => {
        if (disabled) setIsOpen(false)
    }, [disabled])

    return (
        <Dropdown open={isOpen} onOpenChange={setIsOpen} disabled={disabled}>
            <Dropdown.Trigger as="div" className="relative pointer-events-auto">
                <ButtonTranslucent
                    Icon={Icons.Stacks}
                    title="Map Layers"
                    isActive={isOpen}
                    isLight={isLight}
                />
            </Dropdown.Trigger>
            <Dropdown.Menu
                unstyled
                position="top-left"
                className="w-[205px] flex flex-col items-start pb-2 rounded-xl border border-gray-700 bg-black-800 shadow-[0_2px_3px_0_rgba(12,12,15,0.90),0_4px_16px_0_rgba(12,12,15,0.95)] backdrop-blur-sm"
                style={{ zIndex: zIndex.MapLayerManager }}
            >
                <div className="w-full flex justify-between items-center px-4 py-3 border-b border-gray-700">
                    <small className="text-gray-50 leading-[18px]">Map Layers</small>
                    <Dropdown.Close as="button" aria-label="Close map layers menu">
                        <Icons.SmallIcon
                            Icon={Icons.XClose}
                            className="text-gray-500 hover:text-white cursor-pointer"
                        />
                    </Dropdown.Close>
                </div>

                <div className="w-full pt-2 flex flex-col gap-3">
                    <div className="flex flex-col">
                        <SectionLabel label="Details" className="px-4 py-[2px]" />
                        {mapFeatures.map((feature) => (
                            <MapFooterLayerCheckboxRow
                                key={feature.label}
                                title={feature.label}
                                isChecked={feature.val}
                                onClick={feature.flip}
                            />
                        ))}
                    </div>

                    <div className="flex flex-col">
                        <SectionLabel label="Overlays" className="px-4 py-[2px]" />
                        {overlayRows.map((row) => (
                            <MapFooterLayerCheckboxRow
                                key={row.key}
                                title={row.title}
                                Icon={row.Icon}
                                isChecked={row.isChecked}
                                onClick={row.onClick}
                                status={row.status}
                                disabled={row.disabled}
                                onMouseEnter={(e) =>
                                    setHoveredOverlay({
                                        key: row.key,
                                        title: row.title,
                                        rect: e.currentTarget.getBoundingClientRect(),
                                        overzoomed: row.overzoomed,
                                    })
                                }
                                onMouseLeave={() => setHoveredOverlay(null)}
                            />
                        ))}
                    </div>

                    <div className="flex flex-col">
                        <SectionLabel label="Base Map" className="px-4 py-[2px]" />
                        {basemapRows.map((background) => (
                            <MapFooterBaseMapRow
                                key={background.label}
                                title={background.title}
                                thumbnailSrc={`/images/maps/${background.label}.jpg`}
                                isSelected={background.isActiveBackground}
                                onClick={background.switchTo}
                            />
                        ))}
                    </div>
                </div>
            </Dropdown.Menu>
            {isOpen && hoveredOverlay && <MapFooterOverlayInfoPanel hovered={hoveredOverlay} />}
        </Dropdown>
    )
}
